/**
 * Bundle builder — theme-owned "My Bundle" panel (replaces the Rebuy Bundle
 * Builder widget; no rebuy.js involved).
 *
 * The product grid is sections/bundle-builder-grid.liquid (server-rendered
 * from the bundle collection). This element (sections/bundle-builder-panel.liquid)
 * keeps the bundle state, shows the discount tiers / progress / savings, and
 * adds the bundle to the cart in ONE /cart/add.js call whose lines carry the
 * line-item properties Rebuy's Shopify Functions discount ("Rebuy Product
 * Discount", ACTIVE on every store) keys off — the same contract
 * snippets/bundle-discount-apply.liquid uses:
 *   _attribution "Rebuy Bundle Builder", _r_added_bundle_session_id (one id
 *   for all lines so the function tiers them together), _widget_id (must be
 *   the Rebuy bundle widget id: the tier config lives in a metafield keyed by
 *   it), _source "Rebuy", _r_bbsi 0.
 * The displayed tiers come from the section's tier blocks and must match the
 * tiers configured on that widget in the Rebuy admin.
 *
 * Money: the panel formats amounts itself (subtotal, savings) from a sample
 * the section renders with Liquid's money filter, so the output matches the
 * active market currency and format.
 *
 * State: sessionStorage holds {variantId, qty} plus a display snapshot; on
 * every load the snapshot is refreshed from the grid's live data-bb-*
 * attributes (Liquid-rendered, market-correct), and the whole state is
 * dropped when the market/currency changed since it was saved.
 */
(function () {
  "use strict";

  const STORAGE_KEY = "gobi-bundle";
  const FLASH_MS = 1300;

  function loadState(scope) {
    try {
      const raw = JSON.parse(window.sessionStorage.getItem(STORAGE_KEY) || "null");
      if (!raw || !Array.isArray(raw.items)) return [];
      if (scope && raw.scope && raw.scope !== scope) return [];
      return raw.items.filter(i => i && i.variantId && i.qty > 0 && typeof i.price === "number");
    } catch (e) {
      return [];
    }
  }

  function saveState(items, scope) {
    try {
      window.sessionStorage.setItem(STORAGE_KEY, JSON.stringify({ scope, items }));
    } catch (e) {
      /* private mode / quota — the panel still works for the page lifetime */
    }
  }

  /**
   * Build a money formatter from a sample rendering of 123456 (cents), e.g.
   * "$1,234.56", "1.234,56 €", "1 234,56 zł", "¥1,235", "CHF 1'234.56".
   * Anything it cannot parse with confidence (3-decimal currencies, markup)
   * falls back to a plain "1234.56".
   */
  function makeMoneyFormatter(sample) {
    const fallback = cents => (cents / 100).toFixed(2);
    const text = String(sample || "");
    if (text.indexOf("<") >= 0) return fallback;
    const match = text.match(/^([^\d]*)(\d[\d\s.,'’  ]*\d|\d)([^\d]*)$/);
    if (!match) return fallback;
    const prefix = match[1];
    const num = match[2];
    const suffix = match[3];
    const digits = num.replace(/\D/g, "");
    let decimals = 0;
    let decimalSep = "";
    let thousandsSep = "";
    if (digits === "123456" && /\D/.test(num.charAt(num.length - 3))) {
      decimals = 2;
      decimalSep = num.charAt(num.length - 3);
      thousandsSep = num.length > 7 ? num.charAt(1) : "";
    } else if (digits === "1235" || digits === "1234") {
      thousandsSep = num.length > 4 ? num.charAt(1) : "";
    } else {
      return fallback;
    }
    return function format(cents) {
      const negative = cents < 0;
      const abs = Math.abs(Math.round(cents));
      const whole = decimals ? Math.floor(abs / 100) : Math.round(abs / 100);
      let out = String(whole);
      if (thousandsSep) out = out.replace(/\B(?=(\d{3})+(?!\d))/g, thousandsSep);
      if (decimals) out += decimalSep + String(abs % 100).padStart(2, "0");
      return (negative ? "-" : "") + prefix + out + suffix;
    };
  }

  function escapeHtml(s) {
    return String(s == null ? "" : s)
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;")
      .replace(/"/g, "&quot;")
      .replace(/'/g, "&#39;");
  }

  // Locale strings arrive with __NAME__ placeholders (the section passes them
  // to the t filter in place of the real values, see bundle-builder-panel.liquid).
  function fill(template, vars) {
    return String(template || "").replace(/__([A-Z]+)__/g, (m, k) => {
      const key = k.toLowerCase();
      return key in vars ? String(vars[key]) : m;
    });
  }

  function newSessionId() {
    if (window.crypto && typeof window.crypto.randomUUID === "function") return window.crypto.randomUUID();
    return `bb-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
  }

  const CHECK_SVG =
    '<svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M20 6 9 17l-5-5"/></svg>';

  class BundleBuilder extends HTMLElement {
    connectedCallback() {
      if (this.booted) return;
      this.booted = true;

      this.strings = this.readStrings();
      this.tiers = this.readTiers();
      this.maxSlots = Math.max(1, parseInt(this.dataset.maxSlots, 10) || 6);
      this.minItems = Math.max(1, parseInt(this.dataset.minItems, 10) || 1);
      this.widgetId = this.dataset.widgetId || "";
      // market + country + currency: prices in the snapshot only make sense here
      this.scope = [this.dataset.market, this.dataset.country, this.dataset.currency].join("|");
      this.format = makeMoneyFormatter(this.dataset.moneySample);
      this.items = loadState(this.scope);
      this.panel = this.querySelector("[data-bundle-panel]");
      this.flashTimers = {};
      this.busy = false;
      this.abort = typeof AbortController === "function" ? new AbortController() : null;

      if (!this.panel || !this.widgetId) return;
      this.mountShell();
      this.refreshFromGrid();
      this.bindGrid();
      this.bindPanel();
      this.render();
    }

    disconnectedCallback() {
      if (this.abort) this.abort.abort();
    }

    readStrings() {
      const el = this.querySelector('script[type="application/json"][data-bundle-strings]');
      try {
        return el ? JSON.parse(el.textContent) : {};
      } catch (e) {
        return {};
      }
    }

    readTiers() {
      let tiers = [];
      try {
        tiers = JSON.parse(this.dataset.tiers || "[]");
      } catch (e) {
        tiers = [];
      }
      return tiers
        .map(t => ({ qty: parseInt(t.qty, 10), pct: parseFloat(t.pct) }))
        .filter(t => t.qty > 0 && t.pct >= 0)
        .sort((a, b) => a.qty - b.qty);
    }

    /**
     * Persistent shell: the re-rendered part, plus live regions that survive
     * re-renders (a live region only announces changes to a node that already
     * existed) and stay visible on mobile even while the panel is collapsed.
     */
    mountShell() {
      this.panel.innerHTML =
        '<div data-bundle-render></div>' +
        '<p class="bundle-builder__notice" role="status" aria-live="polite" data-bundle-notice hidden></p>' +
        '<p class="bundle-builder__error" role="alert" data-bundle-error hidden></p>' +
        '<span class="visually-hidden" aria-live="polite" data-bundle-live></span>';
      this.renderTarget = this.panel.querySelector("[data-bundle-render]");
    }

    // ---- state -------------------------------------------------------------
    count() {
      return this.items.reduce((n, i) => n + i.qty, 0);
    }

    subtotal() {
      return this.items.reduce((n, i) => n + i.price * i.qty, 0);
    }

    activeTier() {
      const c = this.count();
      let active = null;
      this.tiers.forEach(t => {
        if (c >= t.qty) active = t;
      });
      return active;
    }

    nextTier() {
      const c = this.count();
      return this.tiers.find(t => t.qty > c) || null;
    }

    lineSavings(item, tier) {
      // percentage off the whole line, rounded once — how Shopify allocates it
      return tier ? Math.round((item.price * item.qty * tier.pct) / 100) : 0;
    }

    savings() {
      const tier = this.activeTier();
      return tier ? this.items.reduce((n, i) => n + this.lineSavings(i, tier), 0) : 0;
    }

    persist() {
      saveState(this.items, this.scope);
    }

    /** Re-read titles/prices from the live grid so a stored bundle never shows stale numbers. */
    refreshFromGrid() {
      this.items.forEach(item => {
        const control = document.querySelector(`.gobi-bundle-grid [data-bb-variant="${item.variantId}"]`);
        if (!control) return;
        const fresh = this.lineFromGrid(control.closest(".gobi-bundle-grid__item"), control);
        if (fresh) Object.assign(item, fresh);
      });
      this.persist();
    }

    guardBusy() {
      if (!this.busy) return false;
      this.notice(this.strings.adding);
      return true;
    }

    add(item) {
      if (this.guardBusy()) return false;
      if (this.count() >= this.maxSlots) {
        this.notice(fill(this.strings.full, { max: this.maxSlots }));
        return false;
      }
      const existing = this.items.find(i => i.variantId === item.variantId);
      if (existing) existing.qty += 1;
      else this.items.push(Object.assign({ qty: 1 }, item));
      this.persist();
      this.render();
      return true;
    }

    changeQty(variantId, delta) {
      if (this.guardBusy()) return;
      const item = this.items.find(i => i.variantId === variantId);
      if (!item) return;
      if (delta > 0 && this.count() >= this.maxSlots) {
        this.notice(fill(this.strings.full, { max: this.maxSlots }));
        return;
      }
      item.qty += delta;
      if (item.qty <= 0) this.items = this.items.filter(i => i !== item);
      this.persist();
      this.render();
    }

    remove(variantId) {
      if (this.guardBusy()) return;
      this.items = this.items.filter(i => i.variantId !== variantId);
      this.persist();
      this.render();
    }

    // ---- grid (sections/bundle-builder-grid.liquid) ------------------------
    bindGrid() {
      const opts = this.abort ? { signal: this.abort.signal } : false;
      document.addEventListener(
        "click",
        event => {
          const target = event.target;
          if (!target || !target.closest) return;

          const pill = target.closest(".gobi-bb-picker .gobi-rebuy-picker__btn");
          if (pill) {
            if (pill.disabled) return;
            const actions = pill.closest(".gobi-bundle-grid__actions");
            const trigger = actions && actions.querySelector(".gobi-bb-add");
            pill.setAttribute("aria-pressed", "true");
            const added = this.addFromGrid(pill.closest(".gobi-bundle-grid__item"), pill);
            this.closePicker(actions);
            if (added && trigger) this.flash(trigger);
            return;
          }

          const trigger = target.closest(".gobi-bb-add");
          if (trigger) {
            if (trigger.classList.contains("is-added")) return;
            const item = trigger.closest(".gobi-bundle-grid__item");
            if (trigger.dataset.bbVariant) {
              if (this.addFromGrid(item, trigger)) this.flash(trigger);
              return;
            }
            this.openPicker(trigger.closest(".gobi-bundle-grid__actions"));
            return;
          }

          // a click anywhere else closes open pickers
          if (!target.closest(".gobi-bundle-grid__actions")) this.closeAllPickers();
        },
        opts
      );

      document.addEventListener(
        "keydown",
        event => {
          if (event.key === "Escape") this.closeAllPickers();
        },
        opts
      );
    }

    openPicker(actions) {
      if (!actions) return;
      const picker = actions.querySelector(".gobi-bb-picker");
      const trigger = actions.querySelector(".gobi-bb-add");
      if (!picker || !trigger) return;
      this.closeAllPickers();
      picker.querySelectorAll("[aria-pressed='true']").forEach(b => b.setAttribute("aria-pressed", "false"));
      picker.hidden = false;
      trigger.hidden = true;
      trigger.setAttribute("aria-expanded", "true");
      const first = picker.querySelector(".gobi-rebuy-picker__btn:not([disabled])");
      if (first) first.focus();
      else trigger.focus();
    }

    closePicker(actions) {
      if (!actions) return;
      const picker = actions.querySelector(".gobi-bb-picker");
      const trigger = actions.querySelector(".gobi-bb-add");
      const hadFocus = picker && picker.contains(document.activeElement);
      if (picker) picker.hidden = true;
      if (trigger) {
        trigger.hidden = false;
        trigger.setAttribute("aria-expanded", "false");
        if (hadFocus) trigger.focus();
      }
    }

    closeAllPickers() {
      document.querySelectorAll(".gobi-bundle-grid__actions .gobi-bb-picker:not([hidden])").forEach(picker => {
        this.closePicker(picker.closest(".gobi-bundle-grid__actions"));
      });
    }

    /** Build a bundle line from the grid card's data attributes (null when unusable). */
    lineFromGrid(itemEl, control) {
      if (!itemEl || !control) return null;
      const d = itemEl.dataset;
      const price = parseInt(control.dataset.bbPrice, 10);
      if (!control.dataset.bbVariant || isNaN(price)) return null;
      const compare = parseInt(control.dataset.bbCompare, 10);
      return {
        variantId: String(control.dataset.bbVariant),
        productId: d.bbProductId || "",
        handle: d.bbHandle || "",
        title: d.bbTitle || "",
        variantTitle: control.dataset.bbVariantTitle || "",
        url: d.bbUrl || "",
        image: d.bbImage || "",
        price,
        compareAt: !isNaN(compare) && compare > price ? compare : 0
      };
    }

    addFromGrid(itemEl, control) {
      const line = this.lineFromGrid(itemEl, control);
      return line ? this.add(line) : false;
    }

    flash(trigger) {
      if (!trigger.dataset.gobiLabel) trigger.dataset.gobiLabel = (trigger.textContent || "").trim();
      trigger.classList.add("is-added");
      trigger.setAttribute("aria-label", this.strings.added_to_bundle || trigger.dataset.gobiLabel);
      trigger.innerHTML = CHECK_SVG;
      const key = trigger.dataset.bbHandle || "x";
      clearTimeout(this.flashTimers[key]);
      this.flashTimers[key] = setTimeout(() => {
        trigger.classList.remove("is-added");
        trigger.removeAttribute("aria-label");
        trigger.textContent = trigger.dataset.gobiLabel;
      }, FLASH_MS);
    }

    // ---- panel -------------------------------------------------------------
    bindPanel() {
      this.addEventListener("click", event => {
        const target = event.target;
        if (!target || !target.closest) return;
        const toggle = target.closest("[data-bundle-toggle]");
        if (toggle) {
          this.classList.toggle("is-expanded");
          this.syncToggle();
          return;
        }
        const submit = target.closest("[data-bundle-submit]");
        if (submit) {
          this.addToCart();
          return;
        }
        const remove = target.closest("[data-bundle-remove]");
        if (remove) {
          this.remove(remove.dataset.bundleRemove);
          return;
        }
        const qty = target.closest("[data-bundle-qty]");
        if (qty) {
          this.changeQty(qty.dataset.bundleVariant, parseInt(qty.dataset.bundleQty, 10));
        }
      });
    }

    syncToggle() {
      const expanded = this.classList.contains("is-expanded");
      this.querySelectorAll("[data-bundle-toggle]").forEach(btn => {
        btn.setAttribute("aria-expanded", expanded ? "true" : "false");
        const label = btn.querySelector("[data-bundle-toggle-label]");
        if (label) label.textContent = expanded ? this.strings.hide_bundle : this.strings.view_bundle;
      });
    }

    notice(message) {
      const el = this.panel.querySelector("[data-bundle-notice]");
      if (!el) return;
      el.textContent = message || "";
      el.hidden = !message;
      clearTimeout(this.noticeTimer);
      if (message) this.noticeTimer = setTimeout(() => (el.hidden = true), 4000);
    }

    error(message) {
      const el = this.panel.querySelector("[data-bundle-error]");
      if (!el) return;
      el.textContent = message || "";
      el.hidden = !message;
    }

    announce(text) {
      const el = this.panel.querySelector("[data-bundle-live]");
      if (el && el.textContent !== text) el.textContent = text;
    }

    /** Which control had focus (by stable key) so render() can put it back. */
    focusKey() {
      const active = document.activeElement;
      if (!active || !this.renderTarget || !this.renderTarget.contains(active)) return "";
      const el = active.closest("[data-focus-key]");
      return el ? el.dataset.focusKey : "";
    }

    restoreFocus(key) {
      if (!key) return;
      const el = this.renderTarget.querySelector(`[data-focus-key="${key}"]`);
      if (el && !el.disabled) {
        el.focus();
        return;
      }
      // the control is gone (line removed) — keep focus inside the panel
      const head = this.renderTarget.querySelector("[data-bundle-focus-fallback]");
      if (head) head.focus();
    }

    render() {
      const s = this.strings;
      const count = this.count();
      const subtotal = this.subtotal();
      const savings = this.savings();
      const active = this.activeTier();
      const next = this.nextTier();
      const maxTierQty = this.tiers.length ? this.tiers[this.tiers.length - 1].qty : this.maxSlots;
      const fillPct = Math.min(100, Math.round((Math.min(count, maxTierQty) / maxTierQty) * 100));
      const countText = fill(count === 1 ? s.item_one : s.items, { count });
      const focusKey = this.focusKey();

      let message = "";
      if (active) message = fill(s.tier_unlocked, { percent: active.pct + "%" });
      else if (next) message = fill(s.tier_unlock, { quantity: next.qty, percent: next.pct + "%" });

      const steps = this.tiers
        .map((t, i) => {
          const left = Math.round((t.qty / maxTierQty) * 100);
          const cls = ["bundle-builder__step"];
          if (count >= t.qty) cls.push("is-reached");
          if (i === this.tiers.length - 1) cls.push("is-last");
          return `<li class="${cls.join(" ")}" style="left:${left}%"><span class="bundle-builder__step-pct">${escapeHtml(t.pct)}%</span><span class="bundle-builder__step-label">${escapeHtml(fill(t.qty === 1 ? s.item_one : s.items, { count: t.qty }))}</span></li>`;
        })
        .join("");

      // one row per line; empty rows fill up to the maximum number of units
      const slots = [];
      this.items.forEach(item => {
        const lineTotal = item.price * item.qty;
        const discounted = lineTotal - this.lineSavings(item, active);
        const priceHtml = active
          ? `<del>${escapeHtml(this.format(lineTotal))}</del> <ins>${escapeHtml(this.format(discounted))}</ins>`
          : item.compareAt
            ? `<del>${escapeHtml(this.format(item.compareAt * item.qty))}</del> <ins>${escapeHtml(this.format(lineTotal))}</ins>`
            : escapeHtml(this.format(lineTotal));
        const v = escapeHtml(item.variantId);
        slots.push(
          `<li class="bundle-builder__slot is-filled">` +
            (item.image ? `<img class="bundle-builder__slot-image" src="${escapeHtml(item.image)}" alt="" width="56" height="74" loading="lazy">` : `<span class="bundle-builder__slot-image bundle-builder__slot-image--empty"></span>`) +
            `<div class="bundle-builder__slot-body">` +
            `<p class="bundle-builder__slot-title">${item.url ? `<a href="${escapeHtml(item.url)}">${escapeHtml(item.title)}</a>` : escapeHtml(item.title)}</p>` +
            (item.variantTitle ? `<p class="bundle-builder__slot-variant">${escapeHtml(item.variantTitle)}</p>` : "") +
            `<p class="bundle-builder__slot-price">${priceHtml}</p>` +
            `</div>` +
            `<div class="bundle-builder__slot-controls">` +
            `<div class="bundle-builder__qty" role="group" aria-label="${escapeHtml(s.quantity)}">` +
            `<button type="button" class="bundle-builder__qty-btn" data-bundle-qty="-1" data-bundle-variant="${v}" data-focus-key="dec:${v}" aria-label="${escapeHtml(s.decrease)}">−</button>` +
            `<span class="bundle-builder__qty-value">${item.qty}</span>` +
            `<button type="button" class="bundle-builder__qty-btn" data-bundle-qty="1" data-bundle-variant="${v}" data-focus-key="inc:${v}" aria-label="${escapeHtml(s.increase)}"${count >= this.maxSlots ? " disabled" : ""}>+</button>` +
            `</div>` +
            `<button type="button" class="bundle-builder__remove" data-bundle-remove="${v}" data-focus-key="rm:${v}" aria-label="${escapeHtml(fill(s.remove_item, { title: item.title }))}">${escapeHtml(s.remove)}</button>` +
            `</div>` +
            `</li>`
        );
      });
      for (let i = count; i < this.maxSlots; i++) {
        const tier = this.tiers.find(t => t.qty === i + 1);
        const hint = tier ? fill(s.tier_unlock, { quantity: tier.qty, percent: tier.pct + "%" }) : "";
        slots.push(
          `<li class="bundle-builder__slot is-empty"><span class="bundle-builder__slot-image bundle-builder__slot-image--empty" aria-hidden="true"></span>` +
            `<div class="bundle-builder__slot-body"><p class="bundle-builder__slot-title">${escapeHtml(s.select_product)}</p>` +
            (hint ? `<p class="bundle-builder__slot-hint">${escapeHtml(hint)}</p>` : "") +
            `</div></li>`
        );
      }

      const canSubmit = this.items.length > 0 && count >= this.minItems && !this.busy;
      const total = subtotal - savings;

      this.renderTarget.innerHTML =
        `<div class="bundle-builder__bar">` +
        `<button type="button" class="bundle-builder__toggle" data-bundle-toggle data-focus-key="toggle" aria-expanded="false" aria-controls="BundleBuilderBody">` +
        `<span class="bundle-builder__bar-title">${escapeHtml(s.title)}</span> <span class="bundle-builder__bar-count">${escapeHtml(countText)}</span> <span class="bundle-builder__bar-action" data-bundle-toggle-label>${escapeHtml(s.view_bundle)}</span>` +
        `</button>` +
        `<span class="bundle-builder__bar-total">${escapeHtml(this.format(total))}</span>` +
        `</div>` +
        `<div class="bundle-builder__body" id="BundleBuilderBody">` +
        `<div class="bundle-builder__head"><h2 class="h5 bundle-builder__title" tabindex="-1" data-bundle-focus-fallback>${escapeHtml(s.title)}</h2><span class="bundle-builder__count">${escapeHtml(countText)}</span></div>` +
        `<div class="bundle-builder__scroll">` +
        (this.tiers.length
          ? `<div class="bundle-builder__progress"><div class="bundle-builder__track" role="progressbar" aria-valuemin="0" aria-valuemax="${maxTierQty}" aria-valuenow="${Math.min(count, maxTierQty)}" aria-label="${escapeHtml(s.progress)}"><div class="bundle-builder__fill" style="width:${fillPct}%"></div></div><ol class="bundle-builder__steps">${steps}</ol></div>`
          : "") +
        (message ? `<p class="bundle-builder__message${active ? " is-unlocked" : ""}">${escapeHtml(message)}</p>` : "") +
        `<ul class="bundle-builder__slots">${slots.join("")}</ul>` +
        `</div>` +
        `<dl class="bundle-builder__summary">` +
        `<div class="bundle-builder__summary-row${savings ? "" : " is-muted"}"><dt>${escapeHtml(s.total_savings)}</dt><dd>${savings ? "−" : ""}${escapeHtml(this.format(savings))}</dd></div>` +
        `<div class="bundle-builder__summary-row bundle-builder__summary-row--total"><dt>${escapeHtml(s.subtotal)}</dt><dd>${savings ? `<del>${escapeHtml(this.format(subtotal))}</del> ` : ""}<strong>${escapeHtml(this.format(total))}</strong></dd></div>` +
        `</dl>` +
        `<button type="button" class="button button--filled bundle-builder__submit" data-bundle-submit data-focus-key="submit"${canSubmit ? "" : " disabled"}><span data-bundle-submit-label>${escapeHtml(this.busy ? s.adding : s.add_to_cart)}</span><span class="bundle-builder__submit-price">${escapeHtml(this.format(total))}</span></button>` +
        `</div>`;

      this.classList.toggle("has-items", this.items.length > 0);
      this.syncToggle();
      this.restoreFocus(focusKey);
      this.announce(message ? `${countText} — ${message}` : countText);
    }

    // ---- add to cart -------------------------------------------------------
    async addToCart() {
      if (this.busy || !this.items.length || this.count() < this.minItems) return;
      this.busy = true;
      const submitted = this.items.map(i => ({ variantId: i.variantId, qty: i.qty }));
      const button = this.renderTarget.querySelector("[data-bundle-submit]");
      const label = this.renderTarget.querySelector("[data-bundle-submit-label]");
      if (button) {
        button.disabled = true;
        button.setAttribute("aria-busy", "true");
      }
      if (label) label.textContent = this.strings.adding;
      this.error("");

      const properties = {
        _r_bbsi: 0,
        _r_added_bundle_session_id: newSessionId(),
        _source: "Rebuy",
        _widget_id: this.widgetId,
        _attribution: "Rebuy Bundle Builder"
      };
      const body = {
        items: submitted.map(i => ({ id: Number(i.variantId), quantity: i.qty, properties }))
      };
      const cartDrawer = document.querySelector("cart-drawer");
      if (cartDrawer && typeof cartDrawer.getSectionsToRender === "function") {
        body.sections = cartDrawer.getSectionsToRender().map(section => section.section);
        body.sections_url = window.location.pathname;
      }

      let failed = "";
      let response = null;
      let json = {};
      try {
        const url = (window.routes && window.routes.cart_add_url) || "/cart/add";
        response = await fetch(url, {
          method: "POST",
          headers: { "Content-Type": "application/json", Accept: "application/json", "X-Requested-With": "XMLHttpRequest" },
          body: JSON.stringify(body)
        });
        json = await response.json().catch(() => ({}));
        if (!response.ok || json.status) failed = json.description || json.message || this.strings.error;
      } catch (e) {
        failed = this.strings.error;
      }

      this.busy = false;
      if (!failed) {
        // drop exactly what was submitted; anything added meanwhile stays
        submitted.forEach(sub => {
          const item = this.items.find(i => i.variantId === sub.variantId);
          if (!item) return;
          item.qty -= sub.qty;
          if (item.qty <= 0) this.items = this.items.filter(i => i !== item);
        });
        this.persist();
        this.classList.remove("is-expanded");
        if (cartDrawer && json.sections && typeof cartDrawer.renderContents === "function") {
          cartDrawer.renderContents(json);
        } else if (typeof window.updateCartCounters === "function") {
          window.updateCartCounters();
        }
      }
      this.render();
      if (failed) this.error(failed);
      else this.notice(this.strings.added);
    }
  }

  if (!customElements.get("bundle-builder")) customElements.define("bundle-builder", BundleBuilder);
})();
